import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { userPanelApi, landingPagesApi } from 'api';
import { useScope } from 'contexts/scopeContext';
import { GroupedChoiceList, Question } from 'types/Question';
import { Answer, Application } from 'types/Application';
import { ApplicationPayload } from 'types/payloads/ApplicationPayload';
import FormQuestion from 'components/landingPages/components/Question';
import ConfirmDialog from 'components/_common/ConfirmDialog';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useGlobal } from 'contexts/globalContext';
import { Spinner } from 'components/_new/Spinner';
import PageMissing from 'components/PageMissing';
import { useAuth } from 'contexts/authContext';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Button } from 'components/_new/Button';
import { getRandomInt } from 'utils/number';
import AutoClearFormErrors from './AutoClearFormErrors';

export interface ApplyMembershipFormObject {
	form: Answer[];
}

const formatedValues = (rawValue: any) => {
	if (rawValue === 'true') {
		return {
			value: true,
			mgoptions: undefined,
			moptions: undefined,
		};
	}
	if (rawValue === 'false') {
		return {
			value: false,
			mgoptions: undefined,
			moptions: undefined,
		};
	}
	if (Number.isInteger(rawValue)) {
		return {
			value: Number(rawValue),
			mgoptions: undefined,
			moptions: undefined,
		};
	}
	if (Array.isArray(rawValue)) {
		if (typeof rawValue[0] === 'string') {
			return {
				value: undefined,
				mgoptions: undefined,
				moptions: rawValue as string[],
			};
		}
		return {
			value: undefined,
			mgoptions: rawValue as GroupedChoiceList[],
			moptions: undefined,
		};
	}
	return {
		value: rawValue,
		mgoptions: undefined,
		moptions: undefined,
	};
};

interface FormQuestionsProps {
	setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
	type: 'mentor' | 'mentee';
	programMembershipId: number;
	questionsData: Question[];
	membershipData: Application;
}

const FormQuestions: FC<FormQuestionsProps> = (props) => {
	const { t } = useTranslation();
	const { organizationName, programName } = useParams();

	const { currentUser } = useAuth();
	const { toastRef } = useGlobal();
	const { currentProgramMembership, currentProgram, applicationsRefetch } = useScope();

	const [isLoading, setIsLoading] = useState(true);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [isError, setIsError] = useState(false);
	const { setIsSubmitted, type, programMembershipId, questionsData, membershipData } = props;

	const { data: userPairs } = useQuery(
		['userPairsCurrent', currentProgram, type],
		() => userPanelApi.getCurrentPairs(String(type), Number(currentProgramMembership?.id)),
		{
			enabled: !!currentProgramMembership,
		}
	);

	const hasApplication = Boolean(membershipData);

	// temp form
	const applicationFormKey = `application-form__${type}-${currentProgramMembership?.id || '0'}`;
	const newTempId = useMemo(() => {
		if (membershipData) {
			return null;
		}
		return getRandomInt(10000, 99999);
	}, [membershipData]);
	const [tempId, setTempId] = useLocalStorage<number | null>(`${applicationFormKey}__tempId`);
	useEffect(() => {
		if (!tempId || !String(tempId).length) {
			setTempId(newTempId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tempId, newTempId]);

	const [temp, setTemp] = useLocalStorage<Record<number, any>>(applicationFormKey);

	const saveTempData = useCallback(
		(questionId: number, value: any) => {
			if (value instanceof File) {
				// skip files
				return;
			}
			if (!hasApplication) {
				setTemp((prev) => {
					// add to temp if value is positive and has length
					if (
						(typeof value === 'string' && String(value).length > 0) ||
						typeof value === 'number' ||
						typeof value === 'boolean' ||
						(Array.isArray(value) && value.length > 0)
					) {
						return { ...prev, [questionId]: value };
					}

					// remove from temp
					return Object.fromEntries(Object.entries(prev).filter(([k]) => Number(k) !== questionId));
				});
			}
		},
		[hasApplication, setTemp]
	);

	const { control, handleSubmit, reset, trigger, setValue, getValues, setError, clearErrors } =
		useForm<ApplyMembershipFormObject>({
			defaultValues: {
				form: questionsData.map((question, index) => {
					const value = !hasApplication ? temp?.[question.id] : '';

					// fill firstname
					if (
						currentUser &&
						(!value || String(value).length === 0) &&
						index === questionsData.findIndex((qd) => qd.systemQuestionType === 'name')
					) {
						return {
							value: currentUser.firstName,
							questionId: question.id,
						};
					}

					// fill lastname
					if (
						currentUser &&
						(!value || String(value).length === 0) &&
						index === questionsData.findIndex((qd) => qd.systemQuestionType === 'surname')
					) {
						return {
							value: currentUser.lastName,
							questionId: question.id,
						};
					}

					return {
						questionId: question.id,
						...formatedValues(value),
					};
				}),
			},
		});

	const { fields } = useFieldArray({
		name: 'form',
		control,
	});

	const navigate = useNavigate();

	const { mutate: cancelApplication } = useMutation(
		() => landingPagesApi.cancelProgramApplication(programMembershipId, { applicationRole: type }),
		{
			onSuccess: () => {
				navigate(`/${organizationName}/${programName}`);
				applicationsRefetch();
				setTempId(null);
			},
		}
	);

	const { mutate: postFormAnswers, isLoading: isSubmitting } = useMutation(
		(data: ApplicationPayload) => landingPagesApi.postProgramApplications(programMembershipId, data),
		{
			onSuccess: () => {
				setIsSubmitted(true);
				setTemp({});
				setTimeout(() => {
					window.localStorage.removeItem(applicationFormKey);
				}, 100);
				if (currentProgramMembership) {
					applicationsRefetch();
				}
			},
		}
	);

	const { mutateAsync: uploadFile, isLoading: uploadFileLoading } = useMutation(
		({ questionId, file }: { questionId: number; file: File }) =>
			landingPagesApi.uploadProgramApplicationFile(programMembershipId, {
				questionId,
				file,
				applicationRole: type,
				...(tempId ? { tempId } : {}),
			})
	);

	const { mutateAsync: removeFile, isLoading: removeFileLoading } = useMutation(
		({ questionId }: { questionId: number; index: number }) =>
			landingPagesApi.removeProgramApplicationFile(programMembershipId, {
				questionId,
				applicationRole: type,
				...(tempId ? { tempId } : {}),
			}),
		{
			onSuccess: (data, { index }) => {
				setValue(`form.${index}.value`, null);
			},
		}
	);

	// loading current value
	useEffect(() => {
		setIsLoading(true);

		if (currentUser && membershipData) {
			const form = questionsData.map((question, index) => {
				const questionAnswer = membershipData.answers.find((answer) => answer.questionId === question.id);
				const value = questionAnswer?.value ?? '';

				// fill firstname
				if (
					(!value || String(value).length === 0) &&
					index === questionsData.findIndex((qd) => qd.systemQuestionType === 'name')
				) {
					return {
						value: currentUser.firstName,
						questionId: question.id,
					};
				}

				// fill lastname
				if (
					(!value || String(value).length === 0) &&
					index === questionsData.findIndex((qd) => qd.systemQuestionType === 'surname')
				) {
					return {
						value: currentUser.lastName,
						questionId: question.id,
					};
				}

				return {
					questionId: question.id,
					...formatedValues(value),
				};
			});

			reset({
				form,
			});
		}

		setIsLoading(false);
	}, [currentUser, questionsData, membershipData, reset]);

	const handleFormData = (event: any) => {
		event.preventDefault();
		trigger(undefined, { shouldFocus: true }).then((valid) => {
			if (!valid) {
				toastRef?.current?.show({
					severity: 'error',
					life: 3000,
					summary: t('misc.error'),
					detail: t('landingPages.firstFillAllFieldsInForm'),
				});
			} else {
				handleSubmit((data: ApplyMembershipFormObject) => {
					const preparedPayload = {
						...(tempId ? { tempId } : {}),
						applicationRole: type,
						answers: data.form,
					};
					postFormAnswers(preparedPayload);
				})(event);
			}
		});
	};

	const canCancel = userPairs?.length === 0;

	if (isError) {
		return <PageMissing />;
	}

	if (fields.length !== questionsData.length || isLoading) {
		return <Spinner />;
	}

	return (
		<form className="flex flex-column row-gap-4 landing-page-content" onSubmit={handleFormData}>
			<AutoClearFormErrors control={control} clearErrors={clearErrors} />
			{fields.map((field, index) => (
				<FormQuestion
					key={field.id}
					field={field}
					index={index}
					questionsData={questionsData}
					setIsError={setIsError}
					control={control}
					setValue={setValue}
					getValues={getValues}
					setError={setError}
					onChangeValue={(value) => saveTempData(field.questionId, value)}
					fileActionLoading={uploadFileLoading || removeFileLoading}
					onFileUpload={uploadFile}
					onFileRemove={removeFile}
				/>
			))}
			<div className="grid justify-content-end">
				<div className="col-12 md:col-8 lg:col-6">
					<Button submit label={t('landingPages.buttons.sendForm')} loading={isSubmitting} block />
				</div>
			</div>
			{!!membershipData && membershipData.approved !== 'rejected' && (
				<div className="flex justify-content-end">
					<Button
						label={t('landingPages.buttons.cancelFormApplication')}
						disabled={!canCancel}
						onClick={() => setConfirmationOpen(true)}
						className="underline"
						variant="primary-text"
						tooltip={!canCancel ? t('landingPages.cantWithdrawTooltip') : undefined}
						tooltipClassName="max300px"
					/>
				</div>
			)}
			<ConfirmDialog
				confirmationOpen={confirmationOpen}
				setConfirmationOpen={setConfirmationOpen}
				handleAccept={cancelApplication}
			/>
		</form>
	);
};

export default FormQuestions;
